import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, Mail, Shield, ShieldOff, Trash2, ArrowLeft, Copy, CheckCircle } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Modal } from '@shared/components/ui/Modal';

interface User {
  id: string;
  email: string | null;
  full_name: string | null;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

interface EmailTemplate {
  to: string;
  subject: string;
  temporaryPassword: string;
  loginUrl: string;
  userGuideUrl: string;
}

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showEmailTemplateModal, setShowEmailTemplateModal] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((user) => {
        const email = user.email || '';
        const fullName = user.full_name || '';
        const searchLower = searchTerm.toLowerCase();

        return email.toLowerCase().includes(searchLower) ||
               fullName.toLowerCase().includes(searchLower);
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Call the edge function to get all users with their emails
      const { data, error } = await supabase.functions.invoke('get-all-users');

      if (error) throw error;

      if (data && data.users) {
        setUsers(data.users);
        setFilteredUsers(data.users);
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          is_admin: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;

      toast.success(`Admin status ${!currentStatus ? 'granted' : 'revoked'} successfully`);
      loadUsers();
    } catch (error: any) {
      console.error('Error updating admin status:', error);
      toast.error('Failed to update admin status');
    }
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inviteEmail || !inviteName) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setSendingInvite(true);

      console.log('Sending invite for:', { email: inviteEmail, name: inviteName });

      const { data, error } = await supabase.functions.invoke('send-invite', {
        body: {
          email: inviteEmail,
          fullName: inviteName
        }
      });

      console.log('Invite response:', { data, error });

      // Check for network/invocation errors
      if (error) {
        console.error('Edge function invocation error:', error);

        // Parse error details from FunctionsHttpError
        let errorMessage = 'Failed to connect to server';
        if (error.message) {
          errorMessage = error.message;
        }
        // Check if there's context with more details
        if (error.context) {
          console.error('Error context:', error.context);
        }

        throw new Error(errorMessage);
      }

      // Check response data
      if (!data) {
        console.error('No data received from server');
        throw new Error('No response received from server');
      }

      // Check if operation was successful
      if (!data.success) {
        const errorMsg = data.error || 'Failed to send invitation';
        console.error('Invite failed:', errorMsg, data);

        // Provide user-friendly error messages
        if (errorMsg.includes('already exists')) {
          throw new Error('This email is already registered. The user should use password reset to access their account.');
        }

        throw new Error(errorMsg);
      }

      console.log('Invite successful:', data);

      // Store email template and show it to the admin
      if (data.email_content) {
        setEmailTemplate(data.email_content);
        setShowEmailTemplateModal(true);
      }

      toast.success(`User created successfully! Please send the email manually.`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');

      // Reload user list to show the new user
      await loadUsers();
    } catch (error: any) {
      console.error('Error sending invite:', error);

      // Display user-friendly error message
      const userMessage = error.message || 'Failed to send invitation. Please try again.';
      toast.error(userMessage);
    } finally {
      setSendingInvite(false);
    }
  };

  const deleteUser = async (userId: string, userEmail: string | null) => {
    if (!confirm(`Are you sure you want to delete user ${userEmail || 'this user'}? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Starting user deletion process for:', userId, userEmail);

      const { data, error } = await supabase.functions.invoke('delete-user-account', {
        body: { userId }
      });

      console.log('Delete user response:', { data, error });

      if (error) {
        console.error('Edge function invocation error:', error);

        // Parse error details from FunctionsHttpError
        let errorMessage = 'Failed to connect to delete service';

        if (error.message) {
          errorMessage = error.message;
        }

        // Check if there's context with more details
        if (error.context) {
          console.error('Error context:', error.context);
          if (typeof error.context === 'object') {
            errorMessage += ` - ${JSON.stringify(error.context)}`;
          }
        }

        // Check for specific error patterns
        if (errorMessage.includes('permission') || errorMessage.includes('policy')) {
          errorMessage = 'Permission denied: Unable to delete user. Please check admin privileges.';
        } else if (errorMessage.includes('not found')) {
          errorMessage = 'User not found or already deleted.';
        }

        throw new Error(errorMessage);
      }

      // Check if we got a response
      if (!data) {
        console.error('No response data received from delete function');
        throw new Error('No response received from server. Please try again.');
      }

      // Check if the operation succeeded
      if (!data.success) {
        console.error('Deletion failed with errors:', data);

        // Build a detailed error message
        let errorDetails = 'Failed to delete user';

        if (data.results?.errors && data.results.errors.length > 0) {
          errorDetails = `Deletion errors: ${data.results.errors.join('; ')}`;
        } else if (data.error) {
          errorDetails = data.error;
        } else if (data.message) {
          errorDetails = data.message;
        }

        // Check for partial success
        if (data.results?.steps_completed && data.results.steps_completed.length > 0) {
          console.warn('Partial deletion completed:', data.results.steps_completed);
          errorDetails += ` (Some steps completed: ${data.results.steps_completed.length})`;
        }

        throw new Error(errorDetails);
      }

      // Success! Show a nice summary
      console.log('User deletion completed successfully:', data);

      const summary = data.summary;
      if (summary) {
        const successMessage = `User "${summary.user_email || userEmail}" deleted successfully! ` +
          `Removed ${summary.files_deleted} files and ${summary.records_deleted} database records.`;
        toast.success(successMessage);
      } else {
        toast.success(`User "${userEmail}" deleted successfully!`);
      }

      // Reload the user list
      await loadUsers();
    } catch (error: any) {
      console.error('Error in deleteUser function:', error);

      // Show a user-friendly error message
      const userMessage = error.message || 'An unexpected error occurred while deleting the user. Please try again.';
      toast.error(userMessage);
    }
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success(`${fieldName} copied to clipboard`);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
            <p className="text-gray-600">Manage users and their permissions</p>
          </div>
          <Button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Invite User
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">{user.full_name || 'No name'}</div>
                      <div className="text-xs text-gray-500">{user.email || 'No email'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email || 'No email'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.is_admin
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {user.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => toggleAdminStatus(user.id, user.is_admin)}
                        className="text-blue-600 hover:text-blue-900"
                        title={user.is_admin ? 'Revoke admin' : 'Grant admin'}
                      >
                        {user.is_admin ? (
                          <ShieldOff className="w-4 h-4" />
                        ) : (
                          <Shield className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => deleteUser(user.id, user.email)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No users found</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite New User"
      >
        <form onSubmit={sendInvite} className="space-y-4">
          <div>
            <label htmlFor="inviteName" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <Input
              id="inviteName"
              type="text"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>

          <div>
            <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <Input
              id="inviteEmail"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="john@example.com"
              required
            />
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              disabled={sendingInvite}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              {sendingInvite ? 'Sending...' : 'Send Invitation'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowInviteModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showEmailTemplateModal}
        onClose={() => {
          setShowEmailTemplateModal(false);
          setEmailTemplate(null);
        }}
        title="Email Template for New User"
      >
        {emailTemplate && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Important:</strong> Please copy the information below and send it to the new user via your preferred email client.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Email
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={emailTemplate.to}
                  readOnly
                  className="flex-1 bg-gray-50"
                />
                <Button
                  onClick={() => copyToClipboard(emailTemplate.to, 'Email')}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {copiedField === 'Email' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Subject
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={emailTemplate.subject}
                  readOnly
                  className="flex-1 bg-gray-50"
                />
                <Button
                  onClick={() => copyToClipboard(emailTemplate.subject, 'Subject')}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {copiedField === 'Subject' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temporary Password
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={emailTemplate.temporaryPassword}
                  readOnly
                  className="flex-1 bg-gray-50 font-mono"
                />
                <Button
                  onClick={() => copyToClipboard(emailTemplate.temporaryPassword, 'Password')}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {copiedField === 'Password' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Login URL
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={emailTemplate.loginUrl}
                  readOnly
                  className="flex-1 bg-gray-50"
                />
                <Button
                  onClick={() => copyToClipboard(emailTemplate.loginUrl, 'Login URL')}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {copiedField === 'Login URL' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Guide URL
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={emailTemplate.userGuideUrl}
                  readOnly
                  className="flex-1 bg-gray-50"
                />
                <Button
                  onClick={() => copyToClipboard(emailTemplate.userGuideUrl, 'User Guide URL')}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {copiedField === 'User Guide URL' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Suggested Email Body
              </label>
              <div className="relative">
                <textarea
                  readOnly
                  value={`Hello,

Welcome to FutureLens! Your account has been created.

Here are your login credentials:
- Email: ${emailTemplate.to}
- Temporary Password: ${emailTemplate.temporaryPassword}

Please use the following link to log in:
${emailTemplate.loginUrl}

For your security, you will be prompted to change your password on your first login.

You can find helpful resources and guides here:
${emailTemplate.userGuideUrl}

If you have any questions, please don't hesitate to reach out.

Best regards,
The FutureLens Team`}
                  className="w-full h-64 p-3 border border-gray-300 rounded-md bg-white font-mono text-sm resize-none"
                />
                <Button
                  onClick={() => copyToClipboard(`Hello,

Welcome to FutureLens! Your account has been created.

Here are your login credentials:
- Email: ${emailTemplate.to}
- Temporary Password: ${emailTemplate.temporaryPassword}

Please use the following link to log in:
${emailTemplate.loginUrl}

For your security, you will be prompted to change your password on your first login.

You can find helpful resources and guides here:
${emailTemplate.userGuideUrl}

If you have any questions, please don't hesitate to reach out.

Best regards,
The FutureLens Team`, 'Email Body')}
                  variant="outline"
                  className="absolute top-2 right-2 flex items-center gap-1"
                >
                  {copiedField === 'Email Body' ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy All
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => {
                  setShowEmailTemplateModal(false);
                  setEmailTemplate(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminUsers;
